import React from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "@mui/material";
import moment from "moment";
import BookmarkBorderSharpIcon from "@mui/icons-material/BookmarkBorderSharp";
import BookmarkSharpIcon from "@mui/icons-material/BookmarkSharp";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useTranslation } from "react-i18next";
import JobStatusBadges from "./JobStatusBadges";
import {
  cleanShortDescription,
  companyProfilePath,
  formatPostedAgo,
  truncateJobDescription,
} from "../utils/jobCardHelpers";

const PortalJobCard = ({
  item,
  postedAgo,
  isSaved = false,
  onToggleSave,
  guestMode = false,
  menu = null,
  footer = null,
  hideBookmark = false,
  hideMatchScore = false,
  hideExpiry = false,
  wrapperClassName = "col-lg-4 col-md-6 d-flex align-items-stretch mt-3 IncreaseWidthJobCard",
  wrap = true,
  cardClassName = "",
}) => {
  const { t } = useTranslation();
  const companyHref = companyProfilePath(item);
  const jobHref = item?.jobId ? `/JobDetail/${item.jobId}` : "#";
  const postedLabel = postedAgo || formatPostedAgo(item?.creationTime, t);
  const locationLabel = item?.cityName || item?.location || "";
  const expiryDays = Number(item?.expiringInDays);
  const hasExpiry = Number.isFinite(expiryDays);
  const matchScore = item?.matchScore ?? item?.MatchScore;
  const saveLabel = isSaved ? t("SearchJob.UnsaveJob") : t("SearchJob.SaveJob");

  const companyLogo = (
    <div className="pointer d-flex">
      <img
        src={item?.compLogo}
        alt={`${item?.compName || "Company"} logo`}
        className="job-company-logo"
        title={item?.compName}
      />
    </div>
  );

  const companyName = (
    <span className="d-flex align-items-center" style={{ minWidth: 0 }}>
      <span className="job-company-name" title={item?.compName}>
        {item?.compName}
      </span>
      {item?.isVerified ? (
        <VerifiedIcon
          sx={{ fontSize: 16, color: "#12B76A", marginLeft: "4px" }}
          titleAccess="Verified"
        />
      ) : null}
    </span>
  );

  const card = (
    <div className={`jobCard p-3 w-100 d-flex flex-column justify-content-between${cardClassName ? ` ${cardClassName}` : ""}`}>
      <div className="job-card-body">
        <div className="d-flex justify-content-start align-items-center job-company-row">
          {companyHref ? <Link to={companyHref}>{companyLogo}</Link> : companyLogo}
          {companyHref ? (
            <Link to={companyHref} className="job-company-link d-flex align-items-center">
              {companyName}
            </Link>
          ) : (
            <div className="job-company-link d-flex align-items-center">{companyName}</div>
          )}
          {menu ? <div className="ms-auto">{menu}</div> : null}
        </div>

        <div className="d-flex justify-content-between align-items-start mt-2 job-title-row">
          <Link to={jobHref} className="job-title-link">
            <p className="job-title pointer mb-0">{item?.title}</p>
          </Link>
          {!hideBookmark && onToggleSave ? (
            <button
              type="button"
              className={`job-bookmark-btn${isSaved ? " is-saved" : ""}`}
              onClick={onToggleSave}
              disabled={guestMode}
              title={saveLabel}
              aria-label={saveLabel}
              aria-pressed={isSaved}
            >
              {isSaved ? <BookmarkSharpIcon /> : <BookmarkBorderSharpIcon />}
            </button>
          ) : null}
        </div>

        <p
          className="short-Des-jobCard"
          dangerouslySetInnerHTML={{
            __html: cleanShortDescription(
              truncateJobDescription(item?.shortDescription)
            ),
          }}
        />

        {locationLabel ? (
          <div className="job-location">
            <LocationOnOutlinedIcon />
            <span>{locationLabel}</span>
          </div>
        ) : null}

        <div className="d-flex justify-content-between align-items-center mt-2 job-card-foot">
          <Tooltip
            title={
              item?.creationTime
                ? moment.utc(item.creationTime).local().format("DD MMM YYYY")
                : ""
            }
            arrow
          >
            <small>
              <ScheduleOutlinedIcon />
              {t("Manage Job.Posted")} {postedLabel}
            </small>
          </Tooltip>
          {!hideExpiry && hasExpiry ? (
            <div className="badge badge-date">
              {expiryDays < 0
                ? `${t("Manage Job.expired")} ${Math.abs(expiryDays)} ${t("Manage Job.days Ago")}`
                : `${expiryDays} ${t("Manage Job.days Left")}`}
            </div>
          ) : null}
        </div>

        <div className="jobType">
          {!hideMatchScore && matchScore != null ? (
            <span className="badge badge-job badge-match">
              {Math.round(matchScore)}% {t("SearchJob.Match")}
            </span>
          ) : null}
          {item?.jbTypName ? (
            <div className="badge badge-job badge-job-type">{item.jbTypName}</div>
          ) : null}
          {item?.workLocationTypeName ? (
            <div className="badge badge-job badge-job-location">
              {item.workLocationTypeName}
            </div>
          ) : null}
          {item?.designationName ? (
            <div className="badge badge-job badge-job-role">{item.designationName}</div>
          ) : null}
        </div>
        <JobStatusBadges item={item} />
      </div>
      {footer ? <div className="mt-auto job-card-actions">{footer}</div> : null}
    </div>
  );

  if (!wrap) return card;

  return <div className={wrapperClassName}>{card}</div>;
};

export default PortalJobCard;
